/**
 * Theme: the shared rail shuffle.
 *
 * ONE IMPLEMENTATION, EVERY SHUFFLED RAIL. Generalised from
 * assets/ats-home-reviews.js on 18 August 2026, when the product rails needed
 * exactly the same behaviour. That file is deleted; this is the only copy. A
 * second Fisher-Yates in this codebase would be the same mistake as a second
 * product card or a second filter drawer.
 *
 * THE CONTRACT, and it is the only thing a section must get right:
 *   [data-shuffle]   on the scrolling container. That is all.
 * Its ELEMENT CHILDREN are the candidates, and everything past the visible
 * count ships with a `hidden` attribute from Liquid. This picks a fresh random
 * selection of the same size on every load.
 *
 * Children rather than a marker attribute, deliberately: the candidates are
 * already the only children a rail scroller has, and requiring a per-item hook
 * would have meant wrapping each product card in a div. `.hp-rail .pc` sizes
 * the CARD, so a wrapper becomes the flex item and every card loses its width.
 *
 * ============================ WHY IT EXISTS ============================
 *
 * On the reviews rail the job is variety: a returning visitor should not meet
 * the same quotes every time.
 *
 * On a PRODUCT rail it is variety AND protection, and the protection is the
 * load-bearing half. A rail fed by a best-selling collection prints the store's
 * sales ranking in DOM order: scrape the home page once a day and you have the
 * bestseller list and how it moves, for free, which is precisely what the
 * policy in CLAUDE.md withholds from ?sort_by=best-selling and from the public
 * product feed. Serving a shuffled subset of a wider pool means the order on
 * the page carries no ranking information at all. Membership of the pool is
 * still visible, and that is unavoidable while the products are on the page;
 * the ORDER, which is the part that says what sells and what is climbing, is
 * not recoverable.
 *
 * ⚠️ THE SHUFFLE IS NOT THE WHOLE PROTECTION AND MUST NOT BE TREATED AS IT.
 * A client-side shuffle of a server-rendered pool can be undone by anyone who
 * reads the HTML instead of the rendered DOM. The Liquid side is what actually
 * withholds the ranking: the section renders the pool in a deterministic
 * NON-RANKING order (see the sort in sections/core-home-rail.liquid) so the
 * served bytes never carry it either. This file is the variety half, and the
 * defence in depth, not the lock.
 *
 * WHY THE SELECTION IS CLIENT SIDE. It has to be. Liquid renders once per CDN
 * cache generation, so a Liquid-side "random" pick is frozen for every visitor
 * who lands on the same cached copy: the shuffle would be invisible, which is
 * indistinguishable from broken. Shopify offers no per-request randomness that
 * survives page caching. Doing it in the browser is the only way each load
 * genuinely differs, and it costs nothing because every candidate is already in
 * the HTML.
 *
 * NO REQUEST, NO FLASH, NO LAYOUT SHIFT. The pool is already parsed by the time
 * a `defer` script runs, and this runs IMMEDIATELY rather than waiting for
 * DOMContentLoaded, so the reorder lands before first paint on any normal load.
 * The visible count never changes, only which items and in what order, so the
 * rail's geometry is identical before and after and there is nothing to shift.
 * Measured at CLS 0.
 *
 * NO-JS IS A REAL STATE, NOT A FALLBACK. With this file blocked the reader gets
 * the first N items in the server's own order, which is why the server ships a
 * real selection rather than leaving everything hidden for the script to
 * reveal. On a product rail that server order is deliberately alphabetical, so
 * a script-blocked visitor is served a correct rail and still no ranking.
 */
(function () {
  'use strict';

  /**
   * Fisher-Yates, the unbiased one. The naive `sort(() => Math.random() - 0.5)`
   * is NOT a shuffle: the comparator is inconsistent, so the result depends on
   * the engine's sort algorithm and some orders come up far more often than
   * others. On a draw of 18 from 36 that skew is exactly the thing that would
   * make the rail feel repetitive while looking random in a code review, and on
   * a product rail it would leak a bias back toward the served order.
   */
  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  }

  function pick(pool) {
    var items = Array.prototype.slice.call(pool.children);
    if (items.length < 2) return;

    /* How many were visible on arrival IS the setting, read back off the DOM
       rather than duplicated into a data attribute. One source of truth:
       whatever Liquid decided to leave unhidden is what stays unhidden. */
    var visible = items.filter(function (item) {
      return !item.hasAttribute('hidden');
    }).length;
    if (visible < 1 || visible >= items.length) return;

    shuffle(items);

    /* Reorder in the DOM, then hide the tail. A DocumentFragment so the
       container is touched once: appending items one by one to a live parent
       would force a layout on each move. appendChild MOVES an existing node
       rather than copying it, so no listener, no id and no upgraded custom
       element inside an item is lost, which matters on a product card carrying
       a wishlist bookmark. */
    var frag = document.createDocumentFragment();
    items.forEach(function (item, index) {
      if (index < visible) item.removeAttribute('hidden');
      else item.setAttribute('hidden', '');
      frag.appendChild(item);
    });
    pool.appendChild(frag);

    /* The rail measured itself on connectedCallback, possibly before this ran.
       The geometry is unchanged, but the scroll position may not be at zero on
       a bfcache restore, so put it back and let <core-rail> repaint its arrows
       and indicator from the scroll event that follows. */
    pool.scrollLeft = 0;
    pool.dispatchEvent(new Event('scroll'));
  }

  function run() {
    var pools = document.querySelectorAll('[data-shuffle]');
    for (var i = 0; i < pools.length; i++) pick(pools[i]);
  }

  /* Immediately: a deferred script is parsed after the document, so every pool
     is already there. The readyState guard is only for the case where this file
     is ever loaded without `defer`. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
